/**
 * @module services/ollamaClient
 * @description Hosted gateway client for Ember/Rufus.
 * The app server proxies this request to the authenticated Quantrolix AI
 * gateway, so Electron, Capacitor, and browser builds use the same path.
 */

import { buildHeaders } from '../lib/api';

import { hasAiConsent, AiConsentError } from './aiConsent';

const RUFUS_ENDPOINT = import.meta.env.VITE_RUFUS_ENDPOINT ||
    'https://apps.quantrolix.ai/api/fire-pos/api/ai/chat';
export const RUFUS_MODEL = 'openai/gpt-oss-20b';
const CHAT_TIMEOUT_MS = 60000;

/**
 * Return the fixed gateway model. Kept as a compatibility function for
 * callers that previously resolved an installed local Ollama model.
 * @returns {Promise<string>} The gateway model name
 */
export async function resolveOllamaModel() {
    return RUFUS_MODEL;
}

/**
 * Ask the hosted gateway model a question. The client sends only the
 * conversation: the server builds the system prompt from the signed-in
 * restaurant's own menu (multi-tenant spec D8) and ignores any system message.
 * @param {Array<{role: string, content: string}>} messages - Chat history incl. the new user message
 * @returns {Promise<string>} The assistant's reply text
 * @throws {Error} If the gateway is unreachable or returns an empty reply
 */
export async function askLocalOllama(messages) {
    if (!hasAiConsent()) throw new AiConsentError();
    const res = await fetch(RUFUS_ENDPOINT, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
            messages: messages.filter((m) => m.role === 'user' || m.role === 'assistant'),
            model: RUFUS_MODEL,
            temperature: 0.7,
            max_tokens: 800,
        }),
        signal: AbortSignal.timeout(CHAT_TIMEOUT_MS),
    });
    if (!res.ok) throw new Error(`AI gateway request failed: ${res.status}`);

    const data = await res.json();
    const content = data?.message?.content || data?.choices?.[0]?.message?.content;
    if (!content) throw new Error('Empty AI gateway response');
    return content;
}
